import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Quote, ArrowRight } from "lucide-react";

interface LeadershipInsightsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function LeadershipInsightsSection({ items, isLoading }: LeadershipInsightsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="leadership-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Leadership"
          icon={Quote}
          title="Leadership Insights"
          href="/leaders-speak"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary font-heading font-bold text-lg">
                      {item.author?.charAt(0) ?? "?"}
                    </div>
                    <div>
                      <h4 className="text-sm font-heading font-semibold text-foreground">{item.author}</h4>
                      <p className="text-xs text-muted-foreground">{item.authorTitle ?? "Leader"}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3">
                    {typeof item.content === "string" ? item.content.slice(0, 150) : ""}
                  </p>
                  <div className="mt-4">
                    <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-xs gap-1">
                      <Link href={`/news/${item.id}`}>
                        Read more <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Quote} description="No leadership insights yet." />
        )}
      </div>
    </section>
  );
}